/*
 * Shop hub page + the four shop groups (Surplus / Survival Gear / Modern Equipment / Other).
 *
 * Adds a new 'shop' page (snippets/sat-shop-hub.liquid): a Shop All feature
 * above four tall department posters, plus a mini version of that same
 * Shop All banner and department cards under the home page's Featured This
 * Week grid (snippets/sat-manifest.liquid, gated on showHomeShopStrip).
 * Every CTA drops the visitor on the Manifest's full counter with that
 * group already selected. Groups are derived from existing signals (tags,
 * product type, era, vendor, name) rather than by editing any product; the
 * rules live in the config block below. Tag a product 'Other'/'Knives',
 * 'Modern Tactical', or 'Survival Gear' to force its group.
 *
 * Poster title auto-fit: titles are measured with a canvas 2D context
 * against the poster's own clientWidth and given an exact pixel font-size,
 * refit on load, on webfont-ready, and on resize — a vw-based clamp() would
 * desync from the column's real width above the grid wrapper's own capped
 * max-width (1180px), oversizing/cramping longer labels the wider the
 * screen. See fitOne/fitAllTitles.
 *
 * Hover image cycling: each poster (and each home mini department card)
 * picks a small set of visually varied, in-stock, photographed items from
 * its group — diversified by a normalized name key so the set isn't five
 * near-duplicate variants of the same listing — and cycles through them on
 * mouseenter, reverting to the lead image on mouseleave. heroPref per group
 * is tuned toward visually strong lead shots (a full jacket, a plate
 * carrier, a knife/watch) rather than whatever happens to score highest on
 * price/photo-count alone.
 *
 * Classifier: SURVIVAL and OTHER keyword/cat checks run before the
 * deptOverride/era fallback (most of the real Surplus catalog has an era
 * metafield set, so a check placed after that fallback effectively never
 * runs for most of the catalog — this was a real bug, not just a keyword
 * gap; see the SURVIVAL_NAME/SURVIVAL_CATS/OTHER_NAME lists for what a full
 * manual read of the real Surplus list turned up).
 *
 * Merchant (follow-up): "there are knives in surplus for some reason... and
 * Aviation based stuff can go in modern." Two more real gaps the same
 * fallback-order issue above was still masking:
 *
 * 1. Aviation-tagged items (Kore headsets, an ASA kneeboard, a flight
 *    helmet liner) were falling through to the generic deptOverride/era
 *    branch, which only routes to Modern Equipment when the name matches
 *    MODERN_NAME's tactical/armor-flavored keyword list — "headset" and
 *    "kneeboard" aren't tactical gear words, so SURPLUS_HINT's blanket
 *    `!MODERN_NAME.test(n)` fallback was sending them to Surplus by
 *    default. Fixed with a direct deptOverride==='Aviation' check, ahead of
 *    the keyword-matching branches entirely, matching the merchant's own
 *    instruction rather than trying to extend MODERN_NAME to guess every
 *    future aviation-accessory noun.
 *
 * 2. The ~49-product Titan International K Damascus-knife catalog has no
 *    'Knives' tag (unlike the JW SteelCrafts import, which does) and about
 *    a third of its listings are bare internal SKUs as the title — "TD-105",
 *    "TC-001" — with no word OTHER_NAME could ever match. Those were
 *    landing in Surplus by pure default, same bug shape as #1. Fixed with a
 *    vendor check (KNIFE_VENDOR_TAGS, read off the vendor-name tag the
 *    Collective import already carries) that routes the whole vendor into
 *    Other by default — the group this department's own blurb already
 *    names knives under — except for the subset whose titles explicitly
 *    flag camping/bushcraft/utility use (now added to SURVIVAL_NAME), which
 *    reads as Survival Gear instead. Checked against a live export of the
 *    real catalog before deploying: all 49 Titan products now split 43
 *    Other / 6 Survival Gear, zero left in Surplus.
 *
 * Visual design (this pass): earlier versions tried a dark "dossier" panel
 * per poster with a duotone-tinted, vignetted photo mat — merchant feedback
 * was that it looked "awkward" once real product photography (shot on
 * plain white/light studio backgrounds, like nearly everything in this
 * catalog) sat inside it: the white background of the source photo shows
 * through unavoidably (object-fit:contain doesn't crop it away), so a dark
 * frame around a white-background product shot just reads as a bright box
 * dropped into a black hole, not a considered design choice. The theme
 * already solves this exact problem elsewhere — the Featured This Week
 * cards (snippets/sat-manifest.liquid's hf0..hf5) and the full-counter
 * product cards both use a plain cream card with a plain white photo
 * container, a thin neutral border, small corner brackets in an accent
 * color, and a solid dark navy CTA that turns rust-red on hover. Rebuilt
 * the four posters and both Shop All banners on that exact pattern instead
 * of inventing a new one: cream background, dark navy title/text, no photo
 * tint or vignette, corner brackets and the kicker/price/number watermark
 * carrying each department's one accent color, and a solid navy→rust CTA
 * button matching the site's real primary-button convention (see the
 * header's "Request Clearance" button and the Manifest's own, previously
 * unused "Enter The Full Manifest" cue block, which this borrows its whole
 * layout from). No source photography was replaced — there's no way to do
 * new product photography here — but the item-code label moved off the
 * photo itself (it used to overlay the image; now it's a plain rust-colored
 * line above it, matching the hf-card SAT‑#### convention) and heroPref
 * keywords were tuned per department toward the more visually distinctive
 * items in each group.
 *
 * Loaded from layout/theme.liquid AFTER every other patch, so it wraps the
 * final visibleProducts (including random sort). Deep links: #shop,
 * #shop/surplus, #shop/survival, #shop/modern, #shop/other.
 */
(function () {
  'use strict';
  if (!window.SATComponent) { return; }
  var proto = window.SATComponent.prototype;

  var GROUPS = [
    { label: 'Surplus', slug: 'surplus', num: '01', kicker: 'DEPT. 01', title: 'SURPLUS', accent: '#8A5E3C', blurb: 'Uniforms, field gear, load-bearing kit and cold-weather issue from armies around the world.', cta: 'Browse Surplus →', heroPref: /(jacket|coat|parka|bomber)/i },
    { label: 'Survival Gear', slug: 'survival', num: '02', kicker: 'DEPT. 02', title: 'SURVIVAL GEAR', accent: '#3F7A54', blurb: 'Tents, sleep systems, fire, water and first aid — everything for staying alive between stops.', cta: 'Browse Survival Gear →', heroPref: /(tent|first aid|sleeping bag)/i },
    { label: 'Modern Equipment', slug: 'modern', num: '03', kicker: 'DEPT. 03', title: 'MODERN EQUIPMENT', accent: '#3D6E8C', blurb: 'Current-production plate carriers, helmets, optics, hearing protection and tactical boots.', cta: 'Browse Modern Equipment →', heroPref: /(plate carrier|helmet)/i },
    { label: 'Other', slug: 'other', num: '04', kicker: 'DEPT. 04', title: 'OTHER', accent: '#A6402A', blurb: 'Books, watches, knives, collectibles and everything else on file that is not issue gear.', cta: 'Browse Other →', heroPref: /(watch|knife|bowie|kukri|vinyl)/i }
  ];
  var BY_LABEL = {}, BY_SLUG = {};
  GROUPS.forEach(function (g) { BY_LABEL[g.label] = g; BY_SLUG[g.slug] = g; });

  var OTHER_CATS = /^(books|vinyl|mil-blox|watches|sunglasses|historic artifacts|masks & accessories|specimens|pet helmet|coffee|tech diving set|work boots)/i;
  var OTHER_NAME = /(watch|chronograph|sunglass|dive |diving|donut wing|handbook|trading card|coffee|\btea\b|dive mask|snorkel|free dive|scuba|sidemount|tank system|regulator|wetsuit|knife|kukri|(^| )axe|bowie|machete|specimen|meteorite|berlin wall|dog |k9|poster|figurine|puzzle|mil-blox|rpd double sided|s.t.a.r.s. elite rpd|work boot)/i;
  var NOT_OTHER = /(watch cap|watchcap)/i;
  var MODERN_VENDORS = ['HCC Tactical', 'Redemption Tactical', 'Atomic Defense', 'Elite Survival Systems', 'Combat Defense Industries'];
  var MODERN_NAME = /(night vision|pvs-|nvg|plate carrier|body armor|ballistic|level iii|level iv|iiia|helmet|respirator|gas mask|holster|tactical|gen 3|kevlar|molle|chest rig|battle belt|mag pouch|magazine|k19|reaper|thermal|optic|red dot|hearing protection|comtac|peltor|earplug|ifak|tourniquet|composite toe)/i;
  var HARD_MODERN = /(tactical|night vision|plate|armor|helmet|respirator|holster)/i;
  var SURPLUS_HINT = /(vintage|surplus|m-65|m-51|alice|gi-style|usgi|fatigue|bdu|woodland|vietnam|wwii|ww2|army|navy|usmc|nato|flight|ma-1|b-3|shemagh|canvas|wool|field|british|german|french|dutch|swiss|polish|austrian|italian|russian|czech|israeli|bundes|dpm|pcs|ecwcs)/i;
  var SURVIVAL_NAME = /(tent|sleeping bag|sleep system|bivy|bivvy|tourniquet|\bifak\b|trauma (kit|pouch)|first aid|fire start|ferro rod|flint striker|water filter|water purif|lifestraw|life straw|sawyer|compass|paracord|multitool|multi-tool|blanket|space blanket|survival(?!\s+systems)|bug.?out|entrenching|\be-tool\b|canteen|hydration|mess kit|camp stove|backpacking stove|water bottle|snake bite|signal mirror|\bwhistle\b|water storage|match case|\bmatches\b|\btinder\b|\blighter\b|sleeping mat|combat shelter|\bshelter\b|bushcraft|camping)/i;
  var SURVIVAL_CATS = /^(survival|sleeping bag|fire starter|compasses|recovery kit|medical pouches|cookware|blanket)$/i;
  var KNIFE_VENDOR_TAGS = ['Titan International K'];

  function groupOf(p) {
    if (p._sg) { return p._sg; }
    var tags = p.tags || [], n = p.name || '', c = p.cat || '', r;
    function has(t) { return tags.indexOf(t) > -1; }
    var isKnifeVendor = KNIFE_VENDOR_TAGS.some(has);
    if (has('Other') || has('Knives') || c === 'Books') { r = 'Other'; }
    else if (has('Survival Gear')) { r = 'Survival Gear'; }
    else if (p.deptOverride === 'Aviation') { r = 'Modern Equipment'; }
    else if (SURVIVAL_CATS.test(c) || SURVIVAL_NAME.test(n)) { r = 'Survival Gear'; }
    else if (OTHER_CATS.test(c) || (OTHER_NAME.test(n) && !NOT_OTHER.test(n))) { r = 'Other'; }
    else if (isKnifeVendor) { r = 'Other'; }
    else if (has('Modern Tactical')) { r = 'Modern Equipment'; }
    else if (p.deptOverride || p.era) { r = (SURPLUS_HINT.test(n) || !MODERN_NAME.test(n)) ? 'Surplus' : 'Modern Equipment'; }
    else if (MODERN_VENDORS.some(has) || MODERN_NAME.test(n) || MODERN_NAME.test(c)) { r = (SURPLUS_HINT.test(n) && !HARD_MODERN.test(n)) ? 'Surplus' : 'Modern Equipment'; }
    else { r = 'Surplus'; }
    p._sg = r;
    return r;
  }

  function buckets(list) {
    var b = { 'Surplus': [], 'Survival Gear': [], 'Modern Equipment': [], 'Other': [] };
    list.forEach(function (p) { b[groupOf(p)].push(p); });
    return b;
  }

  var SIZE_COLOR_WORDS = /\b(black|brown|white|tan|olive|od green|green|blue|grey|gray|red|orange|khaki|coyote|desert|woodland|multicam|navy|beige|sand|charcoal|small|medium|large|x{1,3}s|x{1,3}l|xs|sm|md|lg|xl|\d+(\.\d+)?\s?(in|cm|mm|inch|oz|lb)s?)\b/gi;
  function normName(n) {
    return (n || '').toLowerCase()
      .replace(/[–—-].*$/, '')
      .replace(SIZE_COLOR_WORDS, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function score(p, pref) {
    var sc = ((p.tags || []).indexOf('featured') > -1 ? 1000 : 0) + ((p.images || []).length * 10) + Math.min(p.price || 0, 300) / 30;
    if (pref && pref.test(p.name || '')) { sc += 5000; }
    return sc;
  }

  // A handful of visually varied, in-stock, photographed items per group,
  // diversified by normalized name so the cycle isn't 5 sizes/colors of the
  // same listing. First entry doubles as the static (non-hover) hero.
  function pickCycleSet(list, pref, n) {
    var eligible = list.filter(function (p) { return p.photo && p.stock > 0; });
    var scored = eligible.map(function (p) { return { p: p, key: normName(p.name), sc: score(p, pref) }; })
      .sort(function (a, b) { return b.sc - a.sc; });
    var seen = {}, out = [];
    scored.forEach(function (x) {
      if (out.length >= n || seen[x.key]) { return; }
      seen[x.key] = true;
      out.push(x.p);
    });
    if (out.length < n) {
      scored.forEach(function (x) {
        if (out.length >= n || out.indexOf(x.p) > -1) { return; }
        out.push(x.p);
      });
    }
    return out;
  }

  function minPrice(list) {
    var inStock = list.filter(function (p) { return p.stock > 0; });
    var src = inStock.length ? inStock : list;
    if (!src.length) { return null; }
    var m = src[0].price;
    src.forEach(function (p) { if (p.price < m) { m = p.price; } });
    return m;
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function scrollToCounter() {
    var tries = 0;
    (function go() {
      var inputs = document.querySelectorAll(`input[placeholder^='Search the counter']`);
      var el = null, i;
      for (i = 0; i < inputs.length; i++) { if (inputs[i].offsetParent) { el = inputs[i]; break; } }
      if (el) { window.scrollTo(0, Math.max(0, el.getBoundingClientRect().top + window.pageYOffset - 150)); }
      else if (tries++ < 25) { setTimeout(go, 60); }
    })();
  }

  function goCounter(self, label, e) {
    if (e && e.preventDefault) { e.preventDefault(); }
    if (self.siteTone) { self.siteTone('c', .05); }
    self.setState({ page: 'manifest', activeCat: label, query: '' });
    window.scrollTo(0, 0);
    scrollToCounter();
  }

  /* ---- title auto-fit: canvas-measured, so it always renders on one line
     regardless of viewport width vs. the grid's own capped column width ---- */
  var __canvas = null;
  function measureWidth(text, px, weight, family) {
    if (!__canvas) { __canvas = document.createElement('canvas'); }
    var ctx = __canvas.getContext('2d');
    ctx.font = weight + ' ' + px + 'px ' + family;
    return ctx.measureText(text).width;
  }
  function fitOne(el, maxPx, minPx) {
    var box = el.parentElement;
    if (!box) { return; }
    var avail = box.clientWidth;
    if (avail < 20) { return; }
    var text = el.textContent || '';
    if (!text) { return; }
    var refPx = 100;
    var w = measureWidth(text, refPx, '700', "'Oswald',sans-serif");
    if (!w) { return; }
    var size = Math.floor(refPx * (avail * 0.97) / w);
    if (size > maxPx) { size = maxPx; }
    if (size < minPx) { size = minPx; }
    el.style.fontSize = size + 'px';
  }
  function fitAllTitles() {
    document.querySelectorAll("[data-m='poster-title-text']").forEach(function (el) { fitOne(el, 76, 24); });
  }

  /* ---- hover image cycling: plain DOM listeners, wired once per element ---- */
  function applyPhoto(photoEl, tagEl, item) {
    if (!photoEl || !item) { return; }
    photoEl.style.backgroundImage = "url('" + item.photo + "')";
    if (tagEl) { tagEl.textContent = 'SAT‑' + item.id; }
  }
  function wireCycleEl(el, getCycle) {
    if (el.__satHoverWired) { return; }
    el.__satHoverWired = true;
    var photoEl = el.querySelector("[data-m='poster-photo'], [data-m='dept-thumb']");
    var tagEl = el.querySelector("[data-m='poster-tag']");
    var timer = null, i = 0;
    el.addEventListener('mouseenter', function () {
      var list = getCycle();
      if (!list || list.length < 2) { return; }
      i = 0;
      if (timer) { clearInterval(timer); }
      timer = setInterval(function () {
        i = (i + 1) % list.length;
        applyPhoto(photoEl, tagEl, list[i]);
      }, 750);
    });
    el.addEventListener('mouseleave', function () {
      if (timer) { clearInterval(timer); timer = null; }
      var list = getCycle();
      if (list && list[0]) { applyPhoto(photoEl, tagEl, list[0]); }
    });
  }
  function wireHoverCycle() {
    document.querySelectorAll("[data-m='shop-poster']").forEach(function (el, idx) {
      wireCycleEl(el, function () { return GROUPS[idx] && GROUPS[idx].__cycle; });
    });
    document.querySelectorAll("[data-m='home-dept-card']").forEach(function (el, idx) {
      wireCycleEl(el, function () { return GROUPS[idx] && GROUPS[idx].__cycle; });
    });
  }

  var __fitTimer = null;
  function scheduleRefresh() {
    if (__fitTimer) { clearTimeout(__fitTimer); }
    __fitTimer = setTimeout(function () { fitAllTitles(); wireHoverCycle(); }, 80);
  }
  window.addEventListener('resize', scheduleRefresh);
  if (window.document.fonts && document.fonts.ready) {
    document.fonts.ready.then(fitAllTitles);
  }

  if (!document.getElementById('sat-shop-hub-css')) {
    var css = document.createElement('style');
    css.id = 'sat-shop-hub-css';
    css.textContent = `
[data-m='shop-hub']{background:#EDE3CF;padding:0 0 64px}
[data-m='shop-wrap']{max-width:1180px;margin:0 auto;padding:34px 32px 0}
[data-m='shop-all'],[data-m='home-shop-all-mini']{display:flex;align-items:center;justify-content:space-between;gap:24px;flex-wrap:wrap;background:#FAF4E6;color:#232E38;cursor:pointer;position:relative;overflow:hidden;border:1px solid rgba(35,46,56,.2);box-shadow:0 8px 20px rgba(35,46,56,.10);transition:transform .18s,box-shadow .18s}
[data-m='shop-all']{padding:32px 38px}
[data-m='shop-all']:hover,[data-m='home-shop-all-mini']:hover{transform:translateY(-3px);box-shadow:0 14px 28px rgba(35,46,56,.16)}
[data-m='shop-all']::after{content:'ALL';position:absolute;right:-8px;bottom:-40px;font:700 158px/1 'Oswald',sans-serif;color:rgba(35,46,56,.045);letter-spacing:-.02em;pointer-events:none;z-index:0}
[data-m='shop-all']:hover [data-m='shop-all-btn'],[data-m='home-shop-all-mini']:hover [data-m='shop-all-btn-mini']{background:#A6402A}
[data-m='shop-all-kicker'],[data-m='home-shop-all-kicker']{position:relative;z-index:1;font:400 11px 'Special Elite',monospace;letter-spacing:.22em;color:#8A5E3C}
[data-m='shop-all-title']{position:relative;z-index:1;font:700 clamp(40px,6.4vw,72px)/.95 'Oswald',sans-serif;text-transform:uppercase;letter-spacing:.01em;margin:8px 0 6px;color:#232E38}
[data-m='shop-all-sub']{position:relative;z-index:1;font:italic 400 14.5px 'Lora',serif;color:#6B6656;max-width:52ch}
[data-m='shop-all-btn'],[data-m='shop-all-btn-mini']{position:relative;z-index:1;font-family:'Poppins',sans-serif;font-weight:600;letter-spacing:.06em;text-transform:uppercase;background:#232E38;color:#F4EFE3;transition:background .18s;white-space:nowrap;border-radius:2px}
[data-m='shop-all-btn']{font-size:12.5px;padding:16px 28px}
[data-m='shop-or']{text-align:center;font:400 10.5px 'Special Elite',monospace;letter-spacing:.24em;color:#8A8272;margin:30px 0 18px}
[data-m='shop-posters']{display:grid;grid-template-columns:repeat(4,1fr);gap:20px}
[data-m='shop-poster']{background:#FAF4E6;border:1px solid rgba(35,46,56,.16);min-height:620px;display:flex;flex-direction:column;padding:22px 20px 20px;cursor:pointer;position:relative;overflow:hidden;box-shadow:0 8px 20px rgba(35,46,56,.09);transition:transform .18s,box-shadow .18s}
[data-m='shop-poster']::before{content:'';position:absolute;top:9px;left:9px;width:15px;height:15px;border-top:2px solid var(--pa,#8A5E3C);border-left:2px solid var(--pa,#8A5E3C);pointer-events:none;z-index:2}
[data-m='shop-poster']::after{content:'';position:absolute;bottom:9px;right:9px;width:15px;height:15px;border-bottom:2px solid var(--pa,#8A5E3C);border-right:2px solid var(--pa,#8A5E3C);pointer-events:none;z-index:2}
[data-m='shop-poster']:hover{transform:translateY(-5px);box-shadow:0 14px 26px rgba(35,46,56,.15)}
[data-m='shop-poster']:hover [data-m='poster-cta']{background:#A6402A}
[data-m='shop-poster']:hover [data-m='poster-photo'] img,[data-m='shop-poster']:hover [data-m='poster-photo']{transform:scale(1.03)}
[data-m='shop-poster']:hover [data-m='poster-num']{opacity:.09}
[data-m='poster-num']{position:absolute;top:-.08em;right:-.04em;font:700 12.5em/1 'Oswald',sans-serif;color:rgba(35,46,56,.045);pointer-events:none;z-index:0;transition:opacity .22s;user-select:none}
[data-m='poster-body']{position:relative;z-index:1;display:flex;flex-direction:column;flex:1;min-height:0}
[data-m='poster-top']{display:flex;justify-content:space-between;gap:10px;font:400 10px 'Special Elite',monospace;letter-spacing:.18em;color:var(--pa,#8A5E3C)}
[data-m='poster-title']{width:100%;margin:14px 0 8px;overflow:hidden}
[data-m='poster-title-text']{display:block;white-space:nowrap;font:700 40px/1.08 'Oswald',sans-serif;text-transform:uppercase;letter-spacing:.005em;color:#232E38}
[data-m='poster-stripe']{height:2px;background:linear-gradient(90deg,var(--pa,#8A5E3C),transparent);opacity:.55;margin-bottom:12px;flex:none}
[data-m='poster-stats']{font:700 10.5px 'Courier Prime',monospace;letter-spacing:.05em;margin-bottom:10px;display:flex;gap:12px;flex-wrap:wrap;color:#6B6656}
[data-m='poster-stats'] b{color:var(--pa,#8A5E3C)}
[data-m='poster-tag']{font-family:'Special Elite',monospace;font-size:10.5px;letter-spacing:.06em;color:#A6402A;margin-bottom:9px}
[data-m='poster-photo']{flex:1;min-height:190px;background-color:#FFFFFF;background-size:contain;background-repeat:no-repeat;background-position:center;border:1px solid rgba(35,46,56,.14);transition:transform .3s,background-image .1s}
[data-m='poster-blurb']{font:italic 400 13.5px/1.55 'Lora',serif;color:#6B6656;margin:14px 2px 16px;min-height:60px}
[data-m='poster-cta']{font-family:'Poppins',sans-serif;font-weight:600;font-size:12px;letter-spacing:.06em;text-transform:uppercase;background:#232E38;color:#F4EFE3;text-align:center;padding:13px 16px;border-radius:2px;transition:background .18s}
@media (max-width:1300px){
  [data-m='shop-posters']{grid-template-columns:repeat(2,1fr) !important}
}
@media (max-width:900px){
  [data-m='shop-posters']{grid-template-columns:1fr !important}
  [data-m='shop-poster']{min-height:480px !important}
  [data-m='shop-wrap']{padding:24px 16px 0 !important}
  [data-m='shop-all']{padding:26px 22px !important}
}
[data-m='home-shop-strip']{max-width:1180px;margin:34px auto 0;padding:0 32px 4px;}
[data-m='home-shop-head']{display:flex;align-items:center;gap:11px;margin-bottom:13px;}
[data-m='home-shop-head'] span:first-child{font-family:'Special Elite',monospace;font-size:9.5px;letter-spacing:.16em;color:#8A5E3C;white-space:nowrap;}
[data-m='home-shop-head'] span:last-child{flex:1;height:1px;background:rgba(35,46,56,.14);display:block;}
[data-m='home-shop-all-mini']{padding:20px 26px;margin-bottom:20px;}
[data-m='home-shop-all-mini']::after{content:'ALL';position:absolute;right:-6px;bottom:-22px;font:700 90px/1 'Oswald',sans-serif;color:rgba(35,46,56,.045);letter-spacing:-.02em;pointer-events:none;z-index:0}
[data-m='home-shop-all-title']{position:relative;z-index:1;font:700 clamp(24px,3.2vw,34px)/1 'Oswald',sans-serif;text-transform:uppercase;letter-spacing:.01em;margin:6px 0 4px;color:#232E38}
[data-m='home-shop-all-sub']{position:relative;z-index:1;font:italic 400 12px 'Lora',serif;color:#6B6656;}
[data-m='shop-all-btn-mini']{font-size:11.5px;padding:12px 20px;}
[data-m='home-shop-or']{text-align:center;font:400 10px 'Special Elite',monospace;letter-spacing:.22em;color:#8A8272;margin:0 0 16px;}
[data-m='home-shop-row']{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;}
[data-m='home-dept-card']{cursor:pointer;position:relative;overflow:hidden;display:flex;gap:12px;align-items:center;padding:14px 16px;background:#FAF4E6;border:1px solid rgba(35,46,56,.14);transition:transform .18s,box-shadow .18s;}
[data-m='home-dept-card']::before{content:'';position:absolute;top:7px;left:7px;width:11px;height:11px;border-top:2px solid var(--pa,#8A5E3C);border-left:2px solid var(--pa,#8A5E3C);pointer-events:none}
[data-m='home-dept-card']::after{content:'';position:absolute;bottom:7px;right:7px;width:11px;height:11px;border-bottom:2px solid var(--pa,#8A5E3C);border-right:2px solid var(--pa,#8A5E3C);pointer-events:none}
[data-m='home-dept-card']:hover{transform:translateY(-3px);box-shadow:0 10px 22px rgba(35,46,56,.16);}
[data-m='dept-thumb']{flex:none;width:56px;height:56px;background-color:#FFFFFF;background-size:contain;background-repeat:no-repeat;background-position:center;border:1px solid rgba(35,46,56,.16);}
[data-m='home-dept-body']{min-width:0;}
[data-m='home-dept-kicker']{font:400 9px 'Special Elite',monospace;letter-spacing:.14em;color:#8A8272;margin-bottom:4px;}
[data-m='home-dept-title']{font:600 14.5px 'Poppins',sans-serif;color:#232E38;margin-bottom:5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
[data-m='home-dept-count']{font:700 11px 'Courier Prime',monospace;color:var(--pa,#8A5E3C);white-space:nowrap;}
@media (max-width:900px){
  [data-m='home-shop-row']{grid-template-columns:repeat(2,1fr);}
}
@media (max-width:480px){
  [data-m='home-shop-row']{grid-template-columns:1fr;}
}
`;
    document.head.appendChild(css);
  }

  var origVP = proto.visibleProducts;
  proto.visibleProducts = function () {
    var g = BY_LABEL[this.state.activeCat];
    if (!g) { return origVP.call(this); }
    var st = this.state, prev = st.activeCat, list;
    st.activeCat = 'All';
    try { list = origVP.call(this); } finally { st.activeCat = prev; }
    return list.filter(function (p) { return groupOf(p) === g.label; });
  };

  var origRV = proto.renderVals;
  proto.renderVals = function () {
    var out = origRV.call(this);
    var self = this, s = this.state;
    var all = this._products || [];
    var b = buckets(all);
    var inStock = all.filter(function (p) { return p.stock > 0; }).length;

    if (Array.isArray(out.cats)) {
      var kept = out.cats.filter(function (c) { return c.label !== 'Modern Tactical' && c.label !== 'Other'; });
      var chips = GROUPS.map(function (g) {
        var active = s.activeCat === g.label;
        return {
          label: g.label,
          style: active ? 'background:#1A1A18;color:#EFEDE6;' : 'background:transparent;color:#1A1A18;',
          tick: active ? '✕' : '',
          chipDark: active ? 'background:#F0C77A;color:#232E38;border:1px solid #F0C77A;' : 'background:rgba(244,239,227,.06);color:#DDD5C4;border:1px solid rgba(240,199,122,.35);',
          chip: active ? 'background:#232E38;color:#F4EFE3;border:1px solid #232E38;' : 'background:transparent;color:#232E38;border:1px solid rgba(35,46,56,.3);',
          pick: function () { self.setState({ activeCat: g.label }); if (self.siteClick) { self.siteClick(); } }
        };
      });
      var at = 0;
      kept.forEach(function (c, i) { if (c.label === 'All') { at = i + 1; } });
      kept.splice.apply(kept, [at, 0].concat(chips));
      out.cats = kept;
    }

    out.showShopHub = s.page === 'shop' ? 'block' : 'none';
    out.shopAllSub = all.length + ' items on file. Every department, one counter.';
    out.shopAllAria = 'Shop all ' + all.length + ' items';
    out.shopAllGo = function (e) { goCounter(self, 'All', e); };
    out.shopAllKey = function (e) { if (e.key === 'Enter' || e.key === ' ') { goCounter(self, 'All', e); } };

    var groupData = GROUPS.map(function (g) {
      var list = b[g.label], mp = minPrice(list);
      var cycle = pickCycleSet(list, g.heroPref, 5).map(function (p) { return { photo: p.photo, id: p.id }; });
      g.__cycle = cycle;
      return {
        g: g, list: list, hero: cycle[0], minPrice: mp,
        inStock: list.filter(function (p) { return p.stock > 0; }).length
      };
    });

    out.shopGroups = groupData.map(function (d) {
      var g = d.g;
      return {
        num: g.num,
        kicker: g.kicker,
        countLabel: d.list.length + ' ITEMS ON FILE',
        title: g.title,
        blurb: g.blurb,
        cta: g.cta,
        aria: 'Shop ' + g.label + ', ' + d.list.length + ' items',
        style: '--pa:' + g.accent + ';',
        priceStat: d.minPrice != null ? ('FROM $' + d.minPrice) : '—',
        stockStat: d.inStock + ' IN STOCK',
        photoStyle: d.hero ? "background-image:url('" + d.hero.photo + "');" : '',
        itemTag: d.hero ? 'SAT‑' + d.hero.id : 'ON FILE',
        go: function (e) { goCounter(self, g.label, e); },
        key: function (e) { if (e.key === 'Enter' || e.key === ' ') { goCounter(self, g.label, e); } }
      };
    });

    out.homeDeptCards = groupData.map(function (d) {
      var g = d.g;
      return {
        style: '--pa:' + g.accent + ';',
        thumbStyle: d.hero ? "background-image:url('" + d.hero.photo + "');" : '',
        kicker: g.kicker,
        title: g.label,
        countLabel: d.list.length + ' on file',
        go: function (e) { goCounter(self, g.label, e); },
        key: function (e) { if (e.key === 'Enter' || e.key === ' ') { goCounter(self, g.label, e); } }
      };
    });
    out.showHomeShopStrip = s.page === 'home' ? 'block' : 'none';

    out.goShop = function (e) {
      if (e && e.preventDefault) { e.preventDefault(); }
      if (self.siteTone) { self.siteTone('c', .05); }
      self.setState({ page: 'shop' });
      window.scrollTo(0, 0);
    };
    out.navKeyShop = function (e) { if (e.key === 'Enter' || e.key === ' ') { out.goShop(e); } };

    if (s.page === 'shop') {
      out.pageKicker = '02 — SHOP';
      out.pageTitle = 'Shop The Counter';
      out.pageBlurb = 'Take the whole counter, or walk straight to the department you came for.';
      out.pageAccent = '#C2793F';
      out.pageBandBg = '#2B2118';
      out.pageGlow = 'rgba(194,121,63,.22)';
      out.pageStats = [
        { label: 'DEPARTMENTS', value: '04', color: '#C9A227' },
        { label: 'ON FILE', value: pad(all.length), color: '#8FE39C' },
        { label: 'IN STOCK', value: pad(inStock), color: '#F0C77A' }
      ];
    }
    if (s.page === 'shop' || s.page === 'home') { scheduleRefresh(); }
    return out;
  };

  var origHashFor = proto._hashFor;
  proto._hashFor = function (s) {
    if (s.page === 'shop') { return '#shop'; }
    if (s.page === 'manifest' && BY_LABEL[s.activeCat]) { return '#shop/' + BY_LABEL[s.activeCat].slug; }
    return origHashFor.call(this, s);
  };

  var origApply = proto.applyHash;
  proto.applyHash = function () {
    var h = decodeURIComponent(location.hash || '');
    if (h === '#shop') { this.setState({ page: 'shop' }); return; }
    if (h.indexOf('#shop/') === 0) {
      var g = BY_SLUG[h.slice(6)];
      if (g) { this.setState({ page: 'manifest', activeCat: g.label, query: '' }); scrollToCounter(); return; }
    }
    origApply.call(this);
  };
})();
